import React from 'react'
import { useSelector } from 'react-redux'
import {
  functionalUpdate,
  getCoreRowModel,
  useReactTable,
  type Column,
  type ColumnDef,
  type RowSelectionState,
  type SortingState,
} from '@tanstack/react-table'

import { getLocation } from 'ducks/router'
import { useDebounce } from 'hooks'
import { getOrm } from 'orm'

const getOrdering =
  ({ mapSortingPropToQueryParam }) =>
  sorting =>
    sorting.map(({ id, desc }) => {
      const paramName = mapSortingPropToQueryParam[id]
      const prefix = desc ? '-' : ''

      return `${prefix}${paramName}`
    })

export type TableQuery = {
  limit: number
  offset: number
  ordering: string[]
  search?: string
  [key: string]: unknown
}

// The column fields the tables read from `columnDef`
type HeaderColumnDef = {
  header?: string
  accessorKey?: string
  alignLeft?: boolean
  alignRight?: boolean
  width?: string
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type TableHeader = Omit<Column<any, unknown>, 'columnDef'> & {
  columnDef: HeaderColumnDef
}

type UseTableParams = {
  /* eslint-disable @typescript-eslint/no-explicit-any */
  columns: ColumnDef<any, any>[]
  data: any[]
  /* eslint-enable @typescript-eslint/no-explicit-any */
  mapSortingPropToQueryParam: Record<string, string>
  pageCount?: number
  rowSelection?: RowSelectionState
  defaultPageSize?: number
  defaultSorting?: SortingState
  search?: string
  filterQuery?: Record<string, unknown>
  fetchAction: (query: TableQuery) => void
  updateQueriesAction: (query: TableQuery) => void
  onRowSelectionChange?: (selection: RowSelectionState) => void
}

type UseTableStateParams = Pick<
  UseTableParams,
  | 'mapSortingPropToQueryParam'
  | 'search'
  | 'filterQuery'
  | 'defaultPageSize'
  | 'defaultSorting'
>

const useTableState = ({
  mapSortingPropToQueryParam,
  search,
  filterQuery,
  defaultPageSize = 25,
  defaultSorting = [],
}: UseTableStateParams) => {
  const { isPending } = useSelector(getOrm)
  const { query = {} } = useSelector(getLocation)

  const debouncedSearch = useDebounce(search)

  const [sorting, setSorting] = React.useState(
    query?.ordering
      ? (typeof query?.ordering === 'string'
          ? [query?.ordering]
          : [...query?.ordering]
        ).map(key => ({
          id: Object.fromEntries(
            Object.entries(mapSortingPropToQueryParam).map(([k, v]) => [v, k]),
          )[key.replace('-', '')],
          desc: key.includes('-') ? true : false,
        }))
      : defaultSorting,
  )
  const [{ pageIndex, pageSize }, setPagination] = React.useState({
    pageIndex: Number(query?.offset / query?.limit) || 0,
    pageSize: Number(query?.limit) || defaultPageSize,
  })
  const [isFetching, setIsFetching] = React.useState(true)

  React.useEffect(() => {
    setPagination({ pageSize, pageIndex: 0 })
  }, [debouncedSearch, filterQuery, pageSize, setPagination])

  React.useEffect(() => {
    setIsFetching(true)
  }, [debouncedSearch, pageIndex, pageSize, setIsFetching])

  return {
    debouncedSearch,
    pageIndex,
    pageSize,
    sorting,
    isFetching,
    isPending,
    setIsFetching,
    setPagination,
    setSorting,
  }
}

export const useTable = ({
  columns,
  data,
  mapSortingPropToQueryParam,
  pageCount,
  rowSelection = {},
  defaultPageSize,
  defaultSorting,
  search,
  filterQuery,
  fetchAction,
  updateQueriesAction,
  onRowSelectionChange,
}: UseTableParams) => {
  const {
    debouncedSearch,
    pageIndex,
    pageSize,
    sorting,
    isFetching,
    isPending,
    setIsFetching,
    setPagination,
    setSorting,
  } = useTableState({
    mapSortingPropToQueryParam,
    search,
    filterQuery,
    defaultPageSize,
    defaultSorting,
  })

  const timeoutId = React.useRef<number | undefined>(undefined)

  const getRowId = React.useCallback(row => row?.id, [])

  const limit = pageSize
  const offset = pageIndex * limit

  const table = useReactTable({
    data: isFetching ? [] : data,
    columns: isFetching ? [] : columns,
    pageCount: Math.ceil(Number(pageCount) / pageSize),
    state: { pagination: { pageIndex, pageSize }, rowSelection, sorting },
    getCoreRowModel: getCoreRowModel(),
    getRowId,
    onPaginationChange: setPagination,
    onRowSelectionChange: updater =>
      onRowSelectionChange?.(functionalUpdate(updater, rowSelection ?? {})),
    onSortingChange: setSorting,
    debugTable: false,
    manualPagination: true,
    manualSorting: true,
  })

  const handleFetch = React.useCallback(() => {
    const ordering = getOrdering({ mapSortingPropToQueryParam })(sorting)

    fetchAction({
      limit,
      offset,
      ordering,
      search: Boolean(debouncedSearch) ? debouncedSearch : undefined,
      ...filterQuery,
    })
  }, [
    debouncedSearch,
    limit,
    mapSortingPropToQueryParam,
    offset,
    sorting,
    fetchAction,
    filterQuery,
  ])

  const handleQueries = React.useCallback(() => {
    const ordering = getOrdering({ mapSortingPropToQueryParam })(sorting)

    updateQueriesAction({
      limit,
      offset,
      ordering,
      search: Boolean(debouncedSearch) ? debouncedSearch : undefined,
      ...filterQuery,
    })
  }, [
    debouncedSearch,
    limit,
    mapSortingPropToQueryParam,
    offset,
    sorting,
    updateQueriesAction,
    filterQuery,
  ])

  React.useEffect(() => {
    setIsFetching(true)

    handleFetch()

    timeoutId.current = window.setTimeout(() => {
      setIsFetching(false)
    }, 750)

    return () => {
      window.clearTimeout(timeoutId.current)
    }
  }, [timeoutId, handleFetch, setIsFetching])

  React.useEffect(() => {
    handleQueries()
  }, [handleQueries])

  return {
    state: {
      pagination: {
        currentPage: table.getState().pagination.pageIndex + 1,
        pageCount: table.getPageCount(),
        perPage: table.getState().pagination.pageSize,
      },
      table: {
        headers: table.getAllColumns() as unknown as TableHeader[],
        model: table.getRowModel(),
        rowSelection: table.getState().rowSelection,
        isAllRowsSelected: table.getIsAllRowsSelected(),
        isFetching: isFetching || isPending,
        isSomeRowsSelected: table.getIsSomeRowsSelected(),
      },
    },
    handlers: {
      pagination: {
        handlePage: pageIndex => table.setPageIndex(pageIndex),
        handlePageSize: event => table.setPageSize(event.target.value),
      },
      table: {
        toggleAllRowsSelectedHandler: table.getToggleAllRowsSelectedHandler(),
      },
    },
  }
}
